import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { Mailbox } from "lucide-mui";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import FormattedDate from "../../../components/FormattedDate.tsx";
import Panel from "../../../components/Panel.tsx";
import RelativeTime from "../../../components/RelativeTime.tsx";
import ProspectHitTimeline from "../../../components/prospects/ProspectHitTimeline.tsx";
import ProspectSourcesCell from "../../../components/prospects/ProspectSourcesCell.tsx";
import ReviewStatusChips from "../../../components/review/ReviewStatusChips.tsx";
import { collectionPath, pathForTenant, useTenant } from "../../../contexts/TenantContext.tsx";
import { formatNumber } from "../../../utils/format.ts";
import StatusChip from "../../../components/StatusChip.tsx";

// Hits shown before "See all".
const RECENT_HITS = 5;

interface ProspectingCardProps {
  companyId: string;
  selectedTenantId: string | null;
}

/**
 * How the selected tenant has prospected this company: where it stands in the
 * Prospects pipeline, what found it and how often, its latest hits, and the
 * tenant's lists it is on. Lists in other tenants are only counted.
 */
export default function ProspectingCard({ companyId, selectedTenantId }: ProspectingCardProps) {
  const { tenantPath } = useTenant();
  const { data: prospects } = useQuery({
    queryKey: keys.prospect.byCompany(selectedTenantId ?? "", companyId),
    queryFn: () =>
      api.getProspects({ tenant_id: selectedTenantId!, master_company_id: companyId, limit: 1 }),
    enabled: !!selectedTenantId && !!companyId,
  });
  const prospect = prospects?.[0];
  const { data: detail } = useQuery({
    queryKey: keys.prospect.detail(prospect?.id ?? ""),
    queryFn: () => api.getProspect(prospect!.id),
    enabled: !!prospect,
  });
  const { data: memberships } = useQuery({
    queryKey: keys.masterCompany.prospectMemberships(companyId),
    queryFn: () => api.getCompanyProspectMemberships(companyId),
    enabled: !!companyId,
  });
  const elsewhere = (memberships ?? []).filter((m) => m.tenant_id !== selectedTenantId);

  if (!prospect && elsewhere.length === 0) return null;
  const hits = detail?.hits ?? [];

  return (
    <Panel
      title="Prospecting"
      count={prospect?.hit_count}
      sx={{ mb: 3 }}
      action={
        prospect && (
          <Button
            component={RouterLink}
            to={tenantPath(`/prospects/inbox?show=all&prospect=${prospect.id}`)}
            size="small"
            startIcon={<Mailbox />}
          >
            Open in inbox
          </Button>
        )
      }
    >
      {prospect ? (
        <Stack spacing={2}>
          <Stack spacing={1}>
            <ReviewStatusChips
              subject={prospect}
              reopenedAt={prospect.reopened_at}
              newHits={prospect.hits_since_decision}
            />
            <ProspectSourcesCell sources={prospect.sources} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Prospected {formatNumber(prospect.hit_count)} {prospect.hit_count === 1 ? "time" : "times"} · first{" "}
              <FormattedDate value={prospect.first_hit_at} /> · last <RelativeTime value={prospect.last_hit_at} />
            </Typography>
          </Stack>
          {hits.length > 0 && (
            <>
              <ProspectHitTimeline hits={hits.slice(0, RECENT_HITS)} prospectId={prospect.id} compact />
              {hits.length > RECENT_HITS && (
                <Link
                  component={RouterLink}
                  to={tenantPath(`/prospects/inbox?show=all&prospect=${prospect.id}`)}
                  variant="body2"
                >
                  See all {formatNumber(hits.length)} hits
                </Link>
              )}
            </>
          )}
          {detail && detail.collections.length > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">Collections</Typography>
              {detail.collections.map((l) => (
                <Stack key={l.entry_id} direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <Link component={RouterLink} to={tenantPath(collectionPath(l.collection_id))} variant="body2" underline="hover">
                    {l.collection_name}
                  </Link>
                  <StatusChip kind="prospect_status" value={l.review_state} variant="outlined" />
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Not prospected for the selected tenant.
        </Typography>
      )}
      {elsewhere.length > 0 && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.5 }}>
          Also on {elsewhere.length} {elsewhere.length === 1 ? "collection" : "collections"} in other tenants:{" "}
          {elsewhere.slice(0, 3).map((m, i) => (
            <span key={m.entry_id}>
              {i > 0 && ", "}
              <Link component={RouterLink} to={pathForTenant(m.tenant_slug, collectionPath(m.collection_id))} underline="hover">
                {m.collection_name}
              </Link>
            </span>
          ))}
          {elsewhere.length > 3 && "…"}
        </Typography>
      )}
    </Panel>
  );
}
